// Navbar.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shared navbar component for the entire KinetiQ app.
//
// USAGE:
//   <Navbar />
//     — landing page (default). Pass scrollContainerRef + onNavClick when the
//       page owns its own scroll container (e.g. KineticLandingPage).
//
//   <Navbar activePage="login" />
//     — logo only + "Back to home" ghost link.
//
//   <Navbar activePage="register" />
//     — logo only + "Already have an account? Sign in" ghost link.
//
//   <Navbar activePage="about" scrollContainerRef={ref} />
//     — About Us page: logo + Home / About Us / Contact Us links + Login / Register CTA.
//
//   <Navbar activePage="contact" />
//     — public Contact Us page (/contact): logo + Home / About Us / Contact Us
//       links + Login / Register CTA.
//
//   <Navbar activePage="app" />
//     — app / dashboard topbar: logo on the left, profile dropdown on the
//       right (full_name, email, Edit Account, Change Password, Logout).
//       Replaces the old MainLayout topbar entirely — no separate component
//       needed.
//
// PAGES THAT USE THIS:
//   • KineticLandingPage  → <Navbar scrollContainerRef={ref} onNavClick={fn} />
//   • Login               → <Navbar activePage="login" />
//   • Register            → <Navbar activePage="register" />
//   • AboutUs             → <Navbar activePage="about" scrollContainerRef={ref} />
//   • Dashboard / others  → wrapped by <MainLayout> which renders
//                           <Navbar activePage="app" /> internally
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import '../styles/Navbar.css';

// ─── Update this import path to match your project structure ─────────────────
import logoSrc from './kinetiq-logo.png';

// Nav link definitions — shown only on the landing page
const LANDING_LINKS = [
  { label: 'Why KinetiQ',  id: 'why'          },
  { label: 'Features',     id: 'features'      },
  { label: 'Demo',         id: 'demo'          },
  { label: 'How it works', id: 'how-it-works'  },
];

// "About Us" is its own page (route /about-us), not a section of the landing
// page, so it is defined separately: it navigates instead of scrolling.
const ABOUT_LINK = { label: 'About Us', to: '/about-us' };

// "Contact Us" is also its own page (route /contact) rather than a landing
// section, so it navigates instead of scrolling. Keep the path in sync with the
// <Route path="/contact" ...> entry in App.js.
const CONTACT_LINK = { label: 'Contact Us', to: '/contact' };

const Navbar = ({
  // Pass the scrollable div ref when the page owns its own scroll container.
  // Omit (or pass null) to fall back to window scroll tracking.
  scrollContainerRef = null,

  // Smooth-scroll handler injected by KineticLandingPage.
  // Ignored on non-landing pages.
  onNavClick = null,

  // 'landing'  → show full nav links + Launch App CTA
  // 'login'    → logo only + "Back to home" ghost link
  // 'register' → logo only + "Sign in" ghost link
  // 'about'    → About Us page: logo + Home / About Us links + Login / Register CTA
  // 'contact'  → public Contact Us page: same links, Contact Us marked current
  // 'app'      → logo (left) + profile dropdown (right) — replaces MainLayout topbar
  activePage = 'landing',
}) => {
  const [scrolled, setScrolled]           = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const navigate  = useNavigate();
  const dropdownRef = useRef(null);

  // ── Derived booleans ────────────────────────────────────────────────────
  const isAuthPage = activePage === 'login' || activePage === 'register';
  const isLanding  = activePage === 'landing';
  const isAbout    = activePage === 'about';
  const isContact  = activePage === 'contact';
  const isApp      = activePage === 'app';

  // ── Profile data (only used in app variant) ─────────────────────────────
  const fullName = isApp ? (localStorage.getItem('full_name') || 'User')           : '';
  const email    = isApp ? (localStorage.getItem('email')     || 'user@example.com') : '';

  // ── Scroll shadow tracking ──────────────────────────────────────────────
  useEffect(() => {
    // App variant: the topbar is fixed; no scroll tracking needed for shadow.
    if (isApp) return;

    const el = scrollContainerRef?.current ?? null;

    if (el) {
      const fn = () => setScrolled(el.scrollTop > 10);
      el.addEventListener('scroll', fn, { passive: true });
      return () => el.removeEventListener('scroll', fn);
    } else {
      const fn = () => setScrolled(window.scrollY > 10);
      window.addEventListener('scroll', fn, { passive: true });
      return () => window.removeEventListener('scroll', fn);
    }
  }, [scrollContainerRef, isApp]);

  // ── Close profile dropdown on outside click ─────────────────────────────
  useEffect(() => {
    if (!isApp) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isApp]);

  // ── Logo click ──────────────────────────────────────────────────────────
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isApp) {
      navigate('/dashboard');
    } else if (onNavClick) {
      onNavClick('top');
    } else {
      navigate('/');
    }
  };

  // ── CTA (Launch App) click ──────────────────────────────────────────────
  const handleLaunchApp = (e) => {
    e.preventDefault();
    if (onNavClick) {
      onNavClick('get-started');
    } else {
      navigate('/login');
    }
  };

  // ── Logout ───────────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ────────────────────────────────────────────────────────────────────────
  // APP / DASHBOARD VARIANT
  // Renders a topbar that is visually consistent with the landing navbar
  // (same height, same logo treatment) but replaces the right side with a
  // profile dropdown instead of nav links or a CTA.
  // ────────────────────────────────────────────────────────────────────────
  if (isApp) {
    return (
      <nav
        className="kq-nav kq-nav--app"
        role="navigation"
        aria-label="App navigation"
      >
        <div className="kq-nav-inner kq-nav-inner--app">

          {/* Logo — links to /dashboard */}
          <a
            href="/dashboard"
            className="kq-nav-logo"
            onClick={handleLogoClick}
            aria-label="KinetiQ — go to dashboard"
          >
            <img
              src={logoSrc}
              alt="KinetiQ"
              className="kq-nav-logo-img"
            />
          </a>

          {/* Profile dropdown */}
          <div
            className="kq-nav-profile"
            onClick={() => setDropdownVisible((v) => !v)}
            ref={dropdownRef}
            aria-haspopup="true"
            aria-expanded={dropdownVisible}
          >
            {/* Trigger row: avatar icon + name */}
            <div className="kq-nav-profile-trigger">
              <FaUserCircle size={26} color="var(--kinetic-blue)" />
              <span className="kq-nav-profile-name">{fullName}</span>
            </div>

            {/* Dropdown panel */}
            {dropdownVisible && (
              <div className="kq-nav-profile-dropdown" role="menu">
                <p className="kq-nav-profile-email">{email}</p>
                <hr className="kq-nav-profile-divider" />
                <button
                  className="kq-nav-profile-item"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); navigate('/edit-account'); }}
                >
                  Edit Account
                </button>
                <button
                  className="kq-nav-profile-item"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); navigate('/change-password'); }}
                >
                  Change Password
                </button>
                <button
                  className="kq-nav-profile-item kq-nav-profile-item--logout"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); handleLogout(); }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>

        </div>
      </nav>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // LANDING / AUTH VARIANTS  (unchanged behaviour)
  // ────────────────────────────────────────────────────────────────────────
  return (
    <nav
      className={[
        'kq-nav',
        scrolled   ? 'kq-nav--scrolled' : '',
        isAuthPage ? 'kq-nav--auth'     : '',
      ].filter(Boolean).join(' ')}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="kq-nav-inner">

        {/* Logo */}
        <a
          href="/"
          className="kq-nav-logo"
          onClick={handleLogoClick}
          aria-label="KinetiQ — go to home"
        >
          <img
            src={logoSrc}
            alt="KinetiQ — Reaction Modeling"
            className="kq-nav-logo-img"
          />
        </a>

        {/* Centre nav links (landing only) */}
        {isLanding && (
          <div className="kq-nav-links" role="list">
            {LANDING_LINKS.map(({ label, id }) => (
              <a
                key={id}
                href={`#${id}`}
                role="listitem"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavClick) onNavClick(id);
                }}
              >
                {label}
              </a>
            ))}
            <a
              href={ABOUT_LINK.to}
              role="listitem"
              onClick={(e) => { e.preventDefault(); navigate(ABOUT_LINK.to); }}
            >
              {ABOUT_LINK.label}
            </a>
            <a
              href={CONTACT_LINK.to}
              role="listitem"
              onClick={(e) => { e.preventDefault(); navigate(CONTACT_LINK.to); }}
            >
              {CONTACT_LINK.label}
            </a>
          </div>
        )}

        {/* Centre nav links (About Us page): Home, the current page, and Contact Us */}
        {(isAbout || isContact) && (
          <div className="kq-nav-links" role="list">
            <a
              href="/"
              role="listitem"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
            >
              Home
            </a>
            <a
              href={ABOUT_LINK.to}
              role="listitem"
              aria-current={isAbout ? 'page' : undefined}
              onClick={(e) => { e.preventDefault(); if (!isAbout) navigate(ABOUT_LINK.to); }}
            >
              {ABOUT_LINK.label}
            </a>
            <a
              href={CONTACT_LINK.to}
              role="listitem"
              aria-current={isContact ? 'page' : undefined}
              onClick={(e) => { e.preventDefault(); if (!isContact) navigate(CONTACT_LINK.to); }}
            >
              {CONTACT_LINK.label}
            </a>
          </div>
        )}

        {/* Right-side action */}
        <div className="kq-nav-actions">

          {(isLanding || isAbout || isContact) && (
            <a href="/login" className="kq-nav-cta" onClick={handleLaunchApp}>
              Login / Register
            </a>
          )}

          {activePage === 'login' && (
            <a
              href="/"
              className="kq-nav-ghost"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
            >
              ← Back to home
            </a>
          )}

          {activePage === 'register' && (
            <a
              href="/login"
              className="kq-nav-ghost"
              onClick={(e) => { e.preventDefault(); navigate('/login'); }}
            >
              Already have an account? Sign in
            </a>
          )}

        </div>

      </div>
    </nav>
  );
};

export default Navbar;